import * as React from 'react'
import { Plus } from 'lucide-react'
import type { EventDetailDto } from '@lib/dto/event.dto'
import type { EventSalesDto } from '@lib/dto/sales-packet.dto'
import { dollarsFromCents } from '@lib/helpers/format'
import { quote } from '@lib/helpers/sales-money'
import { packetScheduleError } from '@lib/helpers/sales-packet'
import { createInvoiceInputSchema } from '@lib/input/sales-packet.input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { DetailSkeleton, ErrorState } from '@/components/ui/states'
import { useToast } from '@/components/ui/toast'
import { FormError } from '@/components/ui/form-error'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useSalesPriceList, useSendInvoice } from '@/hooks/useSales'
import { todayIso } from '@/lib/bookedDays'
import type { SalesPriceListDto } from '@lib/dto/sales-price-list.dto'
import { lineFromItem, linesOf, PacketLines, plusDays, quoteLines, type LineDraft } from './PacketLines'
import { ScheduleEntryRows } from './ScheduleEntryRows'
import { blankEntry, entriesOf, type EntryDraft } from './scheduleDraft'

interface InvoiceBuilderProps {
  event: EventDetailDto
  sales: EventSalesDto
  open: boolean
  onOpenChange: (open: boolean) => void
}

function Builder({ event, sales, priceList, onDone }: { event: EventDetailDto; sales: EventSalesDto; priceList: SalesPriceListDto; onDone: () => void }) {
  const { toast } = useToast()
  const send = useSendInvoice(event.id)
  const today = todayIso()
  const [lines, setLines] = React.useState<LineDraft[]>([])
  const [entries, setEntries] = React.useState<EntryDraft[]>([{ ...blankEntry('Extras', 'rest'), dueKind: 'on_date', date: plusDays(today, 14) }])
  const [notify, setNotify] = React.useState<string[]>(sales.clients.map((c) => c.userId))
  const [error, setError] = React.useState<string | null>(null)
  const offered = priceList.items.filter((item) => item.trackId === null || event.trackIds.includes(item.trackId))
  const total = quote(quoteLines(lines), priceList.rates)

  function handleSend(): void {
    const sent = linesOf(lines)
    if (typeof sent === 'string') return setError(sent)
    if (sent.length === 0) return setError('Add at least one thing.')
    const schedule = entriesOf(entries)
    if (typeof schedule === 'string') return setError(schedule)
    if (event.date === null) return setError('This event needs its date first.')
    const scheduleError = packetScheduleError(schedule, { low: total, high: total }, event.date, today)
    if (scheduleError !== null) return setError(scheduleError)
    const parsed = createInvoiceInputSchema.safeParse({ lines: sent, schedule, notifyUserIds: notify })
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Something here isn’t complete.')
    setError(null)
    send.mutate(parsed.data, {
      onSuccess: () => {
        toast(notify.length === 0 ? 'Invoice added.' : 'Invoice sent.')
        onDone()
      },
      onError: (e) => setError(requestErrorMessage(e)),
    })
  }

  return (
    <>
      <div className="min-h-0 flex-1 space-y-8 overflow-y-auto px-6 py-5">
        <section className="space-y-3">
          <h3 className="m-0 text-sm font-semibold text-ink">What’s added</h3>
          <PacketLines
            lines={lines}
            onChange={setLines}
            items={offered}
            onAddItem={(item) => setLines((all) => [...all, lineFromItem(item, priceList, event.date ?? '', event.guestCount, 'included')])}
            withGroups={false}
            empty="Add the extras, from the price list or typed."
          />
          {lines.length === 0 ? null : <p className="m-0 text-sm text-ink">Comes to {dollarsFromCents(total.totalCents)}, with tax and service charge.</p>}
        </section>
        <section className="space-y-3">
          <h3 className="m-0 text-sm font-semibold text-ink">When it’s paid</h3>
          <ScheduleEntryRows entries={entries} onChange={setEntries} allowDates />
          <Button variant="outline" size="sm" onClick={() => setEntries((all) => [...all, blankEntry(`Payment ${String(all.length + 1)}`, 'fixed')])}>
            <Plus className="h-3.5 w-3.5" />
            Add a payment
          </Button>
        </section>
        <section className="space-y-3">
          <h3 className="m-0 text-sm font-semibold text-ink">Who’s emailed it</h3>
          {sales.clients.length === 0 ? (
            <p className="m-0 text-sm text-ink-muted">This event has no clients to email. The invoice is still added to its payments.</p>
          ) : (
            <ul className="m-0 list-none space-y-1 p-0">
              {sales.clients.map((c) => (
                <li key={c.userId}>
                  <label className="flex items-center gap-2 text-sm text-ink">
                    <Checkbox checked={notify.includes(c.userId)} onCheckedChange={(on) => setNotify((all) => (on ? [...all, c.userId] : all.filter((id) => id !== c.userId)))} />
                    {c.name} <span className="text-ink-muted">{c.email}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
      <div className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-line px-6 py-4">
        {error === null ? null : <FormError className="mr-auto">{error}</FormError>}
        <Button variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button onClick={handleSend} disabled={send.isPending}>
          {send.isPending ? 'Sending…' : 'Send invoice'}
        </Button>
      </div>
    </>
  )
}

/** Invoicing a booked event for extras (docs/sales.spec.md, "After signing"): no signature, paid from its own links. */
export function InvoiceBuilder({ event, sales, open, onOpenChange }: InvoiceBuilderProps) {
  const priceList = useSalesPriceList(event.venueId)

  function body() {
    if (priceList.isError) return <ErrorState message={requestErrorMessage(priceList.error)} onRetry={() => void priceList.refetch()} />
    if (priceList.data === undefined) return <DetailSkeleton />
    return <Builder event={event} sales={sales} priceList={priceList.data} onDone={() => onOpenChange(false)} />
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="max-w-[880px]">
        <SheetHeader>
          <SheetTitle>Send an invoice</SheetTitle>
          <SheetDescription>For extras on {event.name}. It needs no signature; each payment has its own link.</SheetDescription>
        </SheetHeader>
        {body()}
      </SheetContent>
    </Sheet>
  )
}
